/**
 * Deckorate (PowerPoint) frontend tool handler registry.
 *
 * Each key is the `frontend_binding.handler` value from the Cosmos DB tool
 * spec (see apps/backend/ai/cosmos-migrations/seed/20260728120100-ToolRegistry.json).
 * The add-in dispatches incoming `frontend_tool_call` SSE events by this key.
 *
 * These wrap the SAME service functions the existing Deckorate toolbox panel
 * (src/integrations/deckorate/features/**) already uses — no new Office.js
 * logic, just a chat-callable surface over working code. Every arrange/text/
 * color operation acts on whatever shapes are currently selected in
 * PowerPoint, exactly like the panel's own buttons.
 */

import {
  alignShapes,
  centralizeOnSlide,
  arrangeCascade,
  distributeShapes,
  adjustSpacing,
  stackShapes,
  duplicateShapes,
  multiplyObject,
  sliceObject,
  dockShapes,
  swapShapes,
  fitToSlide,
  smartAlign,
  resizeShapes,
  stretchShapes,
  deleteAllText,
  splitTextByParagraphs,
  mergeTextBoxes,
  sortTextAscending,
  sortTextDescending,
  removeLineBreaks,
  reorderShapesByText,
  replaceFont,
  setConnectorWeight,
  tableToTextExplode,
  tableToTextAsIs,
  splitTable,
} from "../../../src/integrations/deckorate/features/FormatTools/services/formatOps";
import {
  applyFillColour,
  applyFontColour,
  applyLineColour,
  applyNoFill,
  applyNoLine,
  applyOnBrandFill,
  shuffleColour,
  detectNonBrandColours,
  replaceColours,
  cleanUpStyling,
  extractThemeColors,
} from "../../../src/integrations/deckorate/features/ColorTools/services/colourOps";
import { extractSelectedSlideContent } from "../../../src/integrations/deckorate/features/SpeakerNotes/services/slideContentExtractor";
import { postSpeakerNotes } from "../../../src/integrations/deckorate/features/SpeakerNotes/services/speakerNotesApi";
import { setSpeakerNotes } from "../../../src/integrations/deckorate/features/SpeakerNotes/services/speakerNotesOffice";
import { createHttpClient } from "../../../src/integrations/deckorate/services/deckorateApi";
import { config } from "../../../src/shared/config/environment";
import { fetchWithRefresh } from "../../../src/shared/api/fetchWithRefresh";
import { emitPills } from "../outlook/pill-bridge";
import { requestDeckorateView, type DeckorateNavTarget } from "./nav-bridge";
import type {
  AlignEdge,
  ArrangeCorner,
  Axis,
  DuplicateDir,
  DockEdge,
  SwapMode,
  SizeMode,
  StretchOp,
  ReorderAxis,
  MultiplyConfig,
  SliceConfig,
  ColourTarget,
} from "../../../src/integrations/deckorate/types";

/** Matches Word's handler contract — a plain (args) => Promise<unknown> function. */
export type HandlerFn = (args: Record<string, unknown>, ctx?: HandlerContext) => Promise<unknown>;

/**
 * Auth/session context threaded in by useChat for handlers that need to call
 * a backend REST endpoint directly rather than just talking to Office.js —
 * e.g. Deckorate's speaker-notes generation (the Deckorate content backend)
 * and Word's word.docreview.scan (AIVA-7506's bulk-create findings
 * endpoint). Pure Office.js handlers (everything else) ignore it. Mirrors
 * useDeckorateHttp's authorizedFetch, but as a plain function since handlers
 * run outside React's render tree.
 */
export interface HandlerContext {
  apiToken: string | null;
  refresh?: () => Promise<string | null>;
  /** The live chat thread id — only word.docreview.scan reads this today. */
  threadId?: string | null;
}

function buildDeckorateHttpClient(ctx: HandlerContext | undefined) {
  const authorizedFetch = (url: string, init: RequestInit = {}) => {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined),
      ...(ctx?.apiToken ? { Authorization: `Bearer ${ctx.apiToken}` } : {}),
    };
    return fetchWithRefresh(url, { ...init, headers }, ctx?.refresh);
  };
  return createHttpClient(authorizedFetch, config.deckorateApiBaseUrl);
}

const _str = (v: unknown): string => (typeof v === "string" ? v : "");
const _num = (v: unknown, fallback: number): number => (typeof v === "number" ? v : fallback);
const _bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const OK = { ok: true } as const;

/**
 * Panels that need choices only the user can make (brand source, scope, a
 * people list, map region) — the LLM hands off to the panel rather than
 * acting itself. Each entry names the `ui.pill.*` tool the LLM calls (renders
 * a button) and the `deckorate.nav.*` handler its click reaches (opens the
 * panel via nav-bridge). `label` matches the panel's own button text in
 * DeckorateHome/ribbonGroups.tsx so the pill and the panel read as the same
 * feature. `target` must be a value DeckorateModule's `View` union accepts.
 */
const PANEL_HANDOFFS: ReadonlyArray<{
  pillTool: string;
  navHandler: string;
  label: string;
  target: DeckorateNavTarget;
}> = [
  { pillTool: "ui.pill.deckorate.apply_brand", navHandler: "deckorate.nav.open_apply_brand", label: "Apply Brand", target: "apply-brand" },
  { pillTool: "ui.pill.deckorate.brand_check", navHandler: "deckorate.nav.open_brand_check", label: "Brand Check", target: "brand-check" },
  { pillTool: "ui.pill.deckorate.map_plot", navHandler: "deckorate.nav.open_map_plot", label: "Plot on Map", target: "map" },
  { pillTool: "ui.pill.deckorate.org_chart", navHandler: "deckorate.nav.open_org_chart", label: "Org Chart", target: "org-chart" },
  { pillTool: "ui.pill.deckorate.convert_infographic", navHandler: "deckorate.nav.open_infographic", label: "Convert to Infographic", target: "infographic" },
  { pillTool: "ui.pill.deckorate.speaker_notes", navHandler: "deckorate.nav.open_speaker_notes", label: "Speaker Notes", target: "speaker-notes" },
];

/** Builds the pill + nav handler pair for every PANEL_HANDOFFS entry. The nav
 *  handler is NOT LLM-callable — it has no ToolRegistry entry and is
 *  reachable only by clicking the pill its sibling entry renders. */
function buildPanelHandoffHandlers(): Record<string, HandlerFn> {
  const entries: Record<string, HandlerFn> = {};
  for (const { pillTool, navHandler, label, target } of PANEL_HANDOFFS) {
    entries[pillTool] = async () => {
      emitPills([{ type: "office_action", label, handler: navHandler, args: {} }]);
      return { rendered: true };
    };
    entries[navHandler] = async () => {
      requestDeckorateView(target);
      return OK;
    };
  }
  return entries;
}

export const handlers: Record<string, HandlerFn> = {
  /* ── Arrange ─────────────────────────────────────────────────────────── */
  "deckorate.arrange.align_shapes": async (args) => {
    await alignShapes(_str(args.edge) as AlignEdge);
    return { aligned: true };
  },
  "deckorate.arrange.centralize_on_slide": async () => {
    await centralizeOnSlide();
    return { centered: true };
  },
  "deckorate.arrange.arrange_cascade": async (args) => {
    await arrangeCascade(_str(args.corner) as ArrangeCorner);
    return { arranged: true };
  },
  "deckorate.arrange.distribute_shapes": async (args) => {
    await distributeShapes(_str(args.axis) as Axis);
    return { distributed: true };
  },
  "deckorate.arrange.adjust_spacing": async (args) => {
    await adjustSpacing(_str(args.axis) as Axis, _str(args.direction) as "increase" | "decrease");
    return { adjusted: true };
  },
  "deckorate.arrange.stack_shapes": async (args) => {
    await stackShapes(_str(args.axis) as Axis);
    return { stacked: true };
  },
  "deckorate.arrange.duplicate_shapes": async (args) => {
    await duplicateShapes(_str(args.direction) as DuplicateDir);
    return { duplicated: true };
  },
  "deckorate.arrange.multiply_object": async (args) => {
    const cfg: MultiplyConfig = {
      rows: _num(args.rows, 1),
      cols: _num(args.cols, 1),
      rowGutter: _num(args.row_gutter, 0),
      colGutter: _num(args.col_gutter, 0),
      sameSize: _bool(args.same_size, true),
      addHeader: _bool(args.add_header, false),
    };
    await multiplyObject(cfg);
    return OK;
  },
  "deckorate.arrange.slice_object": async (args) => {
    const cfg: SliceConfig = {
      rows: _num(args.rows, 1),
      cols: _num(args.cols, 1),
      rowGutter: _num(args.row_gutter, 0),
      colGutter: _num(args.col_gutter, 0),
    };
    await sliceObject(cfg);
    return OK;
  },
  "deckorate.arrange.dock_shapes": async (args) => {
    await dockShapes(_str(args.edge) as DockEdge);
    return { docked: true };
  },
  "deckorate.arrange.swap_shapes": async (args) => {
    await swapShapes(_str(args.mode) as SwapMode);
    return { swapped: true };
  },
  "deckorate.arrange.fit_to_slide": async (args) => {
    await fitToSlide(_bool(args.preserve_aspect, true));
    return { fitted: true };
  },
  "deckorate.arrange.smart_align": async () => {
    await smartAlign();
    return { aligned: true };
  },
  "deckorate.arrange.resize_shapes": async (args) => {
    await resizeShapes(_str(args.mode) as SizeMode);
    return { resized: true };
  },
  "deckorate.arrange.stretch_shapes": async (args) => {
    await stretchShapes(_str(args.op) as StretchOp);
    return { stretched: true };
  },

  /* ── Text ────────────────────────────────────────────────────────────── */
  "deckorate.text.delete_all_text": async () => {
    await deleteAllText();
    return { cleared: true };
  },
  "deckorate.text.split_text_by_paragraphs": async () => {
    await splitTextByParagraphs();
    return OK;
  },
  "deckorate.text.merge_text_boxes": async () => {
    await mergeTextBoxes();
    return { merged: true };
  },
  "deckorate.text.sort_text": async (args) => {
    if (_str(args.direction) === "desc") await sortTextDescending();
    else await sortTextAscending();
    return { sorted: true };
  },
  "deckorate.text.remove_line_breaks": async () => {
    await removeLineBreaks();
    return { cleaned: true };
  },
  "deckorate.text.reorder_shapes_by_text": async (args) => {
    const axis = _str(args.axis) as ReorderAxis;
    await reorderShapesByText(axis || "both");
    return { reordered: true };
  },
  "deckorate.text.replace_font": async (args) => {
    const replacedCount = await replaceFont(_str(args.from_font), _str(args.to_font), _bool(args.all_slides, false));
    return { replaced_count: replacedCount };
  },
  "deckorate.text.set_connector_weight": async (args) => {
    await setConnectorWeight(_num(args.weight, 1));
    return { applied: true };
  },
  "deckorate.text.table_to_text": async (args) => {
    if (_str(args.mode) === "as_is") await tableToTextAsIs();
    else await tableToTextExplode();
    return { converted: true };
  },
  "deckorate.text.split_table": async (args) => {
    await splitTable(_num(args.header_rows, 0), _num(args.split_row, 1));
    return { split: true };
  },

  /* ── Color ───────────────────────────────────────────────────────────── */
  "deckorate.color.apply_fill_colour": async (args) => {
    await applyFillColour(_str(args.hex));
    return { applied: true };
  },
  "deckorate.color.apply_font_colour": async (args) => {
    await applyFontColour(_str(args.hex));
    return { applied: true };
  },
  "deckorate.color.apply_line_colour": async (args) => {
    await applyLineColour(_str(args.hex));
    return { applied: true };
  },
  "deckorate.color.apply_no_fill": async () => {
    await applyNoFill();
    return { applied: true };
  },
  "deckorate.color.apply_no_line": async () => {
    await applyNoLine();
    return { applied: true };
  },
  "deckorate.color.apply_on_brand_fill": async () => {
    const theme = await extractThemeColors();
    await applyOnBrandFill(theme);
    return { applied: true };
  },
  "deckorate.color.shuffle_colour": async () => {
    const theme = await extractThemeColors();
    await shuffleColour(theme);
    return { applied: true };
  },
  "deckorate.color.detect_non_brand_colours": async () => {
    const results = await detectNonBrandColours(null);
    return { count: results.length, colours: results.map((r) => r.hex) };
  },
  "deckorate.color.replace_colours": async (args) => {
    const targets = new Set<ColourTarget>(["fill", "font", "line"]);
    const replacedCount = await replaceColours([{ from: _str(args.from_hex), to: _str(args.to_hex) }], targets);
    return { replaced_count: replacedCount };
  },
  "deckorate.color.clean_up_styling": async () => {
    const result = await cleanUpStyling();
    return { shapes_updated: result.shapesFixed, message: result.message };
  },

  /* ── Speaker notes ───────────────────────────────────────────────────── */
  "deckorate.speaker_notes.generate": async (_args, ctx) => {
    const slideContent = await extractSelectedSlideContent();
    const http = buildDeckorateHttpClient(ctx);
    const notes = await postSpeakerNotes(http, slideContent);
    return { notes };
  },
  "deckorate.speaker_notes.apply": async (args) => {
    const mode = _str(args.mode) === "replace" ? "replace" : "append";
    const applied = await setSpeakerNotes(_str(args.notes), mode);
    return { applied };
  },

  /* ── Panel hand-offs (Apply Brand, Brand Check, Map Plot, Org Chart) ──── */
  ...buildPanelHandoffHandlers(),
};
